import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Loader2, Send, Sparkles, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const sessionId = () => {
  let s = localStorage.getItem("nq_assist_sid");
  if (!s) { s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("nq_assist_sid", s); }
  return s;
};

export default function AssistantChat({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const sid = useRef(sessionId());
  const scroller = useRef(null);

  const runAction = async (a) => {
    if (a.type === "navigate") {
      onNavigate?.(a.screen);
      setOpen(false);
      return;
    }
    if (a.type === "start_paper") {
      try {
        await api.post("/nexfin/runs", { strategy: "NEXFIN_ORB", params: {}, mode: "PAPER", confirm_live: false,
          instrument: { segment: "NSE", underlying: a.underlying, trade_on: "UNDERLYING", lots: 0, qty: 25, product: "MIS", order_type: "MARKET",
            option: { action_mode: "Buy", strike_sel: "ATM", steps: 0, expiry: "weekly" } } });
        toast.success(`Paper bot started on ${a.underlying}`);
        onNavigate?.("live");
        setOpen(false);
      } catch (e) { toast.error(errMsg(e)); }
    }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/nexfin/assistant/suggestions").then((r) => setSuggestions(r.data.suggestions || [])).catch(() => {});
    if (!msgs.length) api.get(`/nexfin/assistant/${sid.current}`).then((r) => setMsgs(r.data.messages || [])).catch(() => {});
  }, [open]);

  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [msgs, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { id: `u${Date.now()}`, role: "user", text: q }]);
    setBusy(true);
    try {
      const r = await api.post("/nexfin/assistant", { session_id: sid.current, message: q });
      setMsgs((m) => [...m, r.data]);
    } catch (e) {
      setMsgs((m) => [...m, { id: `e${Date.now()}`, role: "assistant", text: `⚠️ ${errMsg(e)}` }]);
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button data-testid="assistant-open" onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[80] flex items-center gap-2 h-12 px-4 rounded-full bg-[#22A34A] text-white shadow-lg shadow-[#22A34A]/30 hover:brightness-110 transition-all">
          <Bot className="w-5 h-5" /><span className="text-sm font-semibold">Ask NEXA</span>
        </button>
      )}
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-5 right-5 z-[80] w-[min(92vw,400px)] h-[min(78vh,560px)] flex flex-col rounded-lg border border-[#163056] bg-[#0A1F3A] shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#163056] bg-[#0B2342]">
            <div className="w-7 h-7 rounded-full bg-[#22A34A]/20 grid place-items-center"><Bot className="w-4 h-4 text-[#22A34A]" /></div>
            <div className="leading-tight">
              <div className="text-sm font-bold text-white">NEXA</div>
              <div className="text-[10px] font-mono text-slate-400">Your trading guide</div>
            </div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)} className="ml-auto text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div ref={scroller} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="assistant-messages">
            {!msgs.length && (
              <div className="text-xs text-slate-400 leading-relaxed">
                <p className="flex items-center gap-1.5 text-[#22A34A] font-semibold mb-1"><Sparkles className="w-3.5 h-3.5" /> Hi, I'm NEXA.</p>
                I can explain how NEXFIN TRADE works and walk you through starting your first trade. Ask me anything, or tap a question below.
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#22A34A] text-white" : "bg-[#102A4A] text-slate-200 border border-[#163056]"}`}>{m.text}
                  {m.action && (
                    <button data-testid="assistant-action" onClick={() => runAction(m.action)}
                      className="mt-2 w-full btn-bull h-8 text-[11px] justify-center">{m.action.label || (m.action.type === "navigate" ? "Open screen" : "Start paper bot")}</button>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="rounded-lg px-3 py-2 bg-[#102A4A] border border-[#163056]"><Loader2 className="w-4 h-4 animate-spin text-[#22A34A]" /></div></div>}
          </div>
          {!msgs.length && suggestions.length > 0 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="assistant-suggestions">
              {suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} className="text-[11px] px-2 py-1 rounded-full border border-[#163056] text-slate-300 hover:border-[#22A34A] hover:text-[#22A34A] transition-colors">{s}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 p-2.5 border-t border-[#163056]">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about setup, strategies, going live…" className="field h-9 flex-1 text-xs" />
            <button data-testid="assistant-send" onClick={() => send()} disabled={busy || !input.trim()} className="btn-bull h-9 w-9 grid place-items-center p-0"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
